import React, { useState, useEffect, useRef, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import ProductRow from './ProductRow';
import ProductGridItem from './ProductGridItem';
import usePageView from '../hooks/usePageView';
import { groupCategories } from '../constants/settings';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import { MAX_CONTENT_WIDTH, BORDER_RADIUS, SPACING_ELEMENT, SPACING_COMPACT, SPACING_COMPONENT, SPACING_BUTTON, SPACING_CARD, SPACING_CARD_BODY, SPACING_GROUP, SPACING_SECTION, SPACING_SECTION_LARGE, SPACING_HERO } from '../constants/layout';
import { COLORS } from '../constants/colors';
import {
  CODA_EXTRABOLD_DISPLAY_SIZE,
  CODA_EXTRABOLD_MOBILE_SIZE,
  FONT_LARGE_TITLE,
  FONT_LARGE_TITLE_MOBILE,
  FONT_TITLE,
  FONT_TITLE_MOBILE,
  FONT_TITLE2,
  FONT_TITLE2_MOBILE,
  FONT_TITLE3,
  FONT_TITLE3_MOBILE,
  FONT_HEADLINE,
  FONT_HEADLINE_MOBILE,
  FONT_SUBHEADLINE,
  FONT_BODY,
  FONT_CALLOUT,
  FONT_FOOTNOTE,
  FONT_CAPTION,
  FONT_CAPTION2,
} from '../constants/typography';
import { DISPLAY_STYLE } from '../constants/displayStyle';
import { getFormattedProductPrice } from '../utils/priceFormatter';
import { buildProductPath } from '../utils/productSlug';
import { HomePreferencesContext } from '../contexts/HomePreferencesContext';
import { ProductsContext } from '../contexts/ProductsContext';
import { BookmarksContext } from '../contexts/BookmarksContext';
import { BannersContext } from '../contexts/BannersContext';

const parseDate = value =>
  !value ? null : value.toDate ? value.toDate() : new Date(value);

const isProductNew = (product) => {
  if (!product?.createdDate) return false;
  const created = product.createdDate.toDate ? product.createdDate.toDate() : new Date(product.createdDate);
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  return created > thirtyDaysAgo;
};

// CategorySelect stays the same
// function CategorySelect({ categories, selected, onChange }) {
//   return (
//     <select value={selected} onChange={onChange}>
//       <option value="">All Categories</option>
//       {categories.map(cat => (
//         <option key={cat.id} value={cat.id}>
//           {cat.title}
//         </option>
//       ))}
//     </select>
//   );
// }

// Hero Section Component with Continuous Scrolling
function HeroSection({ products, imageItems, getCoverImage, categories, homePreferences }) {
  const navigate = useNavigate();
  const { showPrices } = useDisplayPreferences();
  // 1) Only keep products that actually have a cover image
  const productsWithImage = products.filter(product => {
    const coverImage = getCoverImage(product);
    return coverImage && coverImage.imageURL && coverImage.imageURL.trim() !== '';
  });

  // 2) Duplicate that filtered list for seamless infinite scroll
  const featuredProducts = [...productsWithImage, ...productsWithImage];

  const carouselRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const scrollTimeout = useRef(null);
  const scrollPos = useRef(0);

  const handleUserScroll = () => {
    setAutoScroll(false);
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    scrollPos.current = carouselRef.current?.scrollLeft || 0;
    scrollTimeout.current = setTimeout(() => setAutoScroll(true), 2000);
  };

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    scrollPos.current = el.scrollLeft;
    let rafId;
    const tick = () => {
      if (autoScroll) {
        scrollPos.current += 0.5;
        const maxScroll = el.scrollWidth / 2;
        if (scrollPos.current >= maxScroll) {
          scrollPos.current -= maxScroll;
        }
        el.scrollLeft = scrollPos.current;
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [autoScroll, featuredProducts.length]);

  // Previously used window.location.href which caused a full browser reload and blank screen flash.
  // Fixed by using React Router's navigate() with buildProductPath() for slug+encoded ID URLs.
  const handleProductClick = product => {
    navigate(buildProductPath(product));
  };

  // Helper: find the first category title for a given product ID
  const findCategoryTitle = productId => {
    const cat = categories.find(
      c => Array.isArray(c.itemIDs) && c.itemIDs.includes(productId)
    );
    return cat ? cat.title : '';
  };

  // Check if there's any hero text to determine overlay
  const hasHeroText = homePreferences?.heroTitle || homePreferences?.heroSubtitle;

  return (
    <div className="hero-section">
      <div className="hero-content">
        {homePreferences?.heroTitle && (
          <h1 className="hero-title">{homePreferences.heroTitle}</h1>
        )}
        {homePreferences?.heroSubtitle && (
          <p className="hero-subtitle hero-subtitle--constrained">
            {homePreferences.heroSubtitle}
          </p>
        )}

        {homePreferences?.showCarousel && (
          <div
            className="hero-carousel-container"
            ref={carouselRef}
            onWheel={handleUserScroll}
            onTouchStart={handleUserScroll}
            onTouchMove={handleUserScroll}
          >
            <div className="hero-carousel">
              {featuredProducts.map((product, index) => {
                const coverImage = getCoverImage(product);
                const categoryTitle = findCategoryTitle(product.id);
                const formattedProductPrice = getFormattedProductPrice(product);
                return (
                  <div
                    key={`${product.id}-${index}`}
                    className="hero-product-card"
                    onClick={() => handleProductClick(product)}
                  >
                    <div className="hero-product-image">
                      {coverImage ? (
                        <img
                          src={coverImage.thumbnailURL || coverImage.imageURL}
                          alt={product.productName}
                          loading="lazy"
                          onLoad={(e) => e.currentTarget.classList.add('loaded')}
                        />
                      ) : (
                        <div className="hero-product-placeholder">
                          <span className="material-icons">image</span>
                        </div>
                      )}
                    </div>
                    <div className="hero-product-info">
                      {categoryTitle && (
                        <div className="hero-product-category">
                          {categoryTitle}
                        </div>
                      )}
                      <h3 className="hero-product-name">{product.productName}</h3>
                      {showPrices && formattedProductPrice && (
                        <p className="hero-product-price">
                          {formattedProductPrice}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .hero-section {
          background: ${homePreferences?.heroBackgroundImageURL
            ? hasHeroText
              ? `linear-gradient(rgba(255, 255, 255, 0.85), rgba(255, 255, 255, 0.85)), url(${homePreferences.heroBackgroundImageURL})`
              : `url(${homePreferences.heroBackgroundImageURL})`
            : COLORS.lightBackground};
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          color: var(--color-text-primary);
          padding: ${SPACING_HERO} 0;
          margin-bottom: 0;
          position: relative;
          overflow: hidden;
          width: 100vw;
          margin-left: calc(-50vw + 50%);
          min-height: 400px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .hero-content {
          position: relative;
          z-index: 2;
          text-align: center;
          color: var(--color-text-primary);
          width: 100%;
        }

        .hero-title {
          font-size: ${CODA_EXTRABOLD_DISPLAY_SIZE};
          font-weight: 600;
          margin: 0 0 ${SPACING_COMPONENT} 0;
          font-family: var(--font-family-base);
          color: var(--color-text-primary);
          text-shadow: none;
        }

        .hero-subtitle {
          font-size: ${FONT_HEADLINE};
          margin: 0 0 ${SPACING_SECTION} 0;
          font-weight: 300;
          color: var(--color-text-secondary);
          padding: 0 ${SPACING_BUTTON};
        }

        .hero-subtitle--constrained {
          max-width: ${MAX_CONTENT_WIDTH};
          margin-left: auto;
          margin-right: auto;
        }

        .hero-carousel-container {
          width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
          position: relative;
          scrollbar-width: none;
          touch-action: pan-x;
        }

        .hero-carousel-container::-webkit-scrollbar {
          display: none;
        }

        .hero-carousel {
          display: flex;
          gap: ${SPACING_GROUP};
        }

        .hero-product-card {
          min-width: 240px;
          width: 240px;
          height: 240px;
          border-radius: ${BORDER_RADIUS};
          border: 0.5px solid var(--color-border);
          overflow: hidden;
          transition: transform 0.3s ease;
          position: relative;
          display: flex;
          flex-direction: column;
          cursor: pointer;
        }

        .hero-product-card:hover img {
          transform: scale(1.05);
        }

        .hero-product-image {
          width: 100%;
          height: 100%;
          position: relative;
          overflow: hidden;
          flex: 1;
          background: linear-gradient(90deg, #3a3a3a 25%, #4a4a4a 50%, #3a3a3a 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s ease-in-out infinite;
        }

        .hero-product-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.5s ease-in, transform 0.3s ease;
        }
        .hero-product-image img.loaded {
          opacity: 1;
        }

        .hero-product-card:hover .hero-product-image img {
          transform: scale(1.05);
        }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        .hero-product-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #3a3a3a;
          color: rgba(255, 255, 255, 0.6);
        }

        .hero-product-placeholder .material-icons {
          font-size: 40px;
        }

        .hero-product-info {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: ${SPACING_BUTTON} ${SPACING_CARD};
          background: linear-gradient(
            to top,
            rgba(0, 0, 0, 0.75) 0%,
            rgba(0, 0, 0, 0.55) 40%,
            rgba(0, 0, 0, 0.3) 70%,
            rgba(0, 0, 0, 0.1) 85%,
            rgba(0, 0, 0, 0) 100%
          );
          color: white;
          border-radius: 0;
          padding-top: ${SPACING_SECTION};
          text-align: left;
        }

        .hero-product-category {
          font-size: ${FONT_CAPTION};
          margin: 0 0 4px 0;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.7);
          font-family: Helvetica, Arial, sans-serif;
          letter-spacing: 0.06em;
          text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.8);
        }

        .hero-product-name {
          font-size: ${FONT_HEADLINE};
          margin: 0 0 4px 0;
          font-family: inherit;
          font-weight: 600;
          line-height: 1.3;
          color: #ffffff;
          text-shadow: 0 1px 4px rgba(0, 0, 0, 0.9), 0 0 12px rgba(0, 0, 0, 0.7);
        }

        .hero-product-price {
          font-size: ${FONT_BODY};
          font-weight: 600;
          color: #4ade80;
          margin: 0;
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
        }

        @media (max-width: 768px) {
          .hero-title {
            font-size: ${CODA_EXTRABOLD_MOBILE_SIZE};
          }

          .hero-subtitle {
            font-size: ${FONT_HEADLINE_MOBILE};
            margin-bottom: ${SPACING_GROUP};
          }

          .hero-product-card {
            min-width: 200px;
            width: 200px;
            height: 200px;
          }

          .hero-product-info {
            padding: ${SPACING_COMPONENT} ${SPACING_BUTTON};
          }

          .hero-product-category {
            font-size: ${FONT_CAPTION2};
            margin-bottom: 2px;
            letter-spacing: 0.05em;
          }

          .hero-product-name {
            font-size: ${FONT_BODY};
          }

          .hero-product-price {
            font-size: ${FONT_CALLOUT};
          }

          .hero-carousel {
            gap: ${SPACING_CARD_BODY};
          }
        }
      `}</style>
    </div>
  );
}

function InstagramSection() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const scriptSrc = 'https://www.instagram.com/embed.js';
    const processEmbeds = () => {
      if (window.instgrm?.Embeds?.process) {
        window.instgrm.Embeds.process();
      }
    };

    let script = document.querySelector(`script[src="${scriptSrc}"]`);
    const handleLoad = () => {
      script?.setAttribute('data-instgrm-loaded', 'true');
      processEmbeds();
    };

    if (script) {
      if (script.getAttribute('data-instgrm-loaded') === 'true') {
        processEmbeds();
      } else {
        script.addEventListener('load', handleLoad);
      }
    } else {
      script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      script.defer = true;
      script.addEventListener('load', handleLoad);
      document.body.appendChild(script);
    }

    return () => {
      script?.removeEventListener('load', handleLoad);
    };
  }, []);

  return (
    <section className="instagram-section">
      <div className="instagram-inner">
        <div className="instagram-copy">
          <p className="instagram-eyebrow">Social</p>
          <h2 className="instagram-title">Catch the daily haul on Instagram</h2>
          <p className="instagram-subtitle">
            Follow <span>@dndseafoodoc</span> for fresh arrivals, behind-the-scenes moments,
            and seasonal specials straight from the market.
          </p>
          <a
            className="instagram-button"
            href="https://www.instagram.com/dndseafoodoc/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Follow us on Instagram
          </a>
        </div>
        <div className="instagram-embed-wrapper">
          <blockquote
            className="instagram-media"
            data-instgrm-permalink="https://www.instagram.com/dndseafoodoc/?utm_source=ig_embed&utm_campaign=loading"
            data-instgrm-version="14"
          >
            <a
              href="https://www.instagram.com/dndseafoodoc/?utm_source=ig_embed&utm_campaign=loading"
              target="_blank"
              rel="noopener noreferrer"
            >
              View this profile on Instagram
            </a>
          </blockquote>
        </div>
      </div>

      <style jsx>{`
        .instagram-section {
          background: linear-gradient(135deg, rgba(13, 13, 13, 0.9), rgba(32, 32, 32, 0.95));
          padding: ${SPACING_SECTION} ${SPACING_CARD};
          padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
          padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
          display: flex;
          justify-content: center;
          border-top: 1px solid var(--color-border);
          border-bottom: 1px solid var(--color-border);
        }

        .instagram-inner {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: ${SPACING_SECTION};
          align-items: center;
          width: 100%;
          max-width: ${MAX_CONTENT_WIDTH};
          color: var(--color-text-primary);
        }

        .instagram-copy {
          display: flex;
          flex-direction: column;
          gap: ${SPACING_BUTTON};
        }

        .instagram-eyebrow {
          margin: 0;
          font-size: ${FONT_CAPTION};
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--color-text-muted);
        }

        .instagram-title {
          margin: 0;
          font-size: ${FONT_TITLE2};
          font-family: var(--font-family-base);
          text-transform: uppercase;
        }

        .instagram-subtitle {
          margin: 0;
          color: var(--color-text-secondary);
          line-height: 1.6;
        }

        .instagram-subtitle span {
          color: var(--color-text-primary);
          font-weight: 600;
        }

        .instagram-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: ${SPACING_COMPONENT} ${SPACING_CARD_BODY};
          border-radius: 999px;
          background: var(--color-accent);
          color: var(--color-accent-text);
          font-weight: 600;
          text-decoration: none;
          width: fit-content;
          transition: background 0.2s ease, transform 0.2s ease;
        }

        .instagram-button:hover,
        .instagram-button:focus {
          background: var(--color-accent-hover);
          transform: translateY(-1px);
        }

        .instagram-embed-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .instagram-embed-wrapper :global(blockquote.instagram-media) {
          margin: 0 !important;
          background-color: var(--color-surface) !important;
          border-radius: 16px !important;
          border: 1px solid var(--color-border) !important;
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.35) !important;
          overflow: hidden;
          max-width: 420px !important;
          width: 100% !important;
        }

        .instagram-embed-wrapper :global(blockquote.instagram-media a) {
          color: var(--color-link) !important;
        }

        @media (max-width: 960px) {
          .instagram-inner {
            grid-template-columns: 1fr;
            text-align: center;
          }

          .instagram-copy {
            align-items: center;
          }

          .instagram-button {
            width: auto;
          }
        }

        @media (max-width: 600px) {
          .instagram-section {
            padding: ${SPACING_GROUP} ${SPACING_CARD};
            padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
            padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
          }

          .instagram-title {
            font-size: ${FONT_TITLE_MOBILE};
          }
        }
      `}</style>
    </section>
  );
}

function ReviewsSection() {
  const reviews = [
    {
      id: 'cameron-l',
      name: 'Cameron L.',
      location: 'Westminster, CA',
      date: 'Apr 20, 2025',
      rating: 5,
      paragraphs: [
        {
          text: 'This is my favorite place in Westminster for fresh seafood...',
          highlight: "The uni here is the freshest I've ever tried.",
        },
        {
          text: "It's a family-owned business...",
          highlight: 'They make you feel like part of the family.',
        },
        {
          text: '',
          highlight: 'If you love seafood, you have to try this place.',
        },
      ],
      traits: {
        icon: 'favorite',
        label: 'Family-owned • Fresh seafood • Friendly service',
      },
    },
    {
      id: 'tony-t',
      name: 'Tony T.',
      location: 'Westminster, CA',
      date: 'Jun 5, 2024',
      rating: 5,
      paragraphs: [
        {
          text: "If you're looking for high quality seafood...",
          highlight: 'Their prices best even some of the Japanese markets.',
        },
        {
          text: '',
          highlight: 'The salmon, scallops and the toro were so amazingly fresh.',
        },
        {
          text: 'We were first time customers...',
          highlight: 'the guy working the Sunday morning was super friendly.',
        },
      ],
      traits: {
        icon: 'price_check',
        label: 'Great prices • Premium quality • Friendly staff',
      },
    },
  ];

  return (
    <section className="reviews-section" aria-labelledby="reviews-heading">
      <div className="reviews-inner">
        <div className="reviews-header">
          <p className="reviews-eyebrow">
            <span className="reviews-eyebrow-icon" aria-hidden="true">
              <i className="fab fa-yelp" />
            </span>
            <span className="reviews-eyebrow-text">Reviews</span>
          </p>
          <h2 id="reviews-heading" className="reviews-title">Community Favorites</h2>
          <p className="reviews-subtitle">
            Guests rave about the hospitality, the ultra-fresh seafood, and the prices that keep them coming back.
          </p>
          <a
            className="reviews-button"
            href="https://www.yelp.com/biz/d-and-d-seafood-westminster-2"
            target="_blank"
            rel="noopener noreferrer"
          >
            Read more reviews on Yelp
          </a>
        </div>

        <div className="reviews-grid">
          {reviews.map(review => (
            <article key={review.id} className="reviews-card">
              <header className="reviews-card-header">
                <div>
                  <h3 className="reviews-name">{review.name}</h3>
                  <p className="reviews-location">{review.location}</p>
                </div>
                <div className="reviews-meta">
                  <div className="reviews-rating" aria-label={`${review.rating} out of 5 stars`}>
                    <span className="sr-only">{`${review.rating} out of 5 stars`}</span>
                    {Array.from({ length: review.rating }).map((_, index) => (
                      <span key={index} aria-hidden="true" className="reviews-star">
                        ★
                      </span>
                    ))}
                  </div>
                  <span className="reviews-date">{review.date}</span>
                </div>
              </header>

              <div className="reviews-body">
                {review.paragraphs.map((paragraph, index) => (
                  <p key={index} className="reviews-paragraph">
                    {paragraph.text && <span>{paragraph.text} </span>}
                    {paragraph.highlight && (
                      <span className="reviews-highlight">{paragraph.highlight}</span>
                    )}
                  </p>
                ))}
              </div>

              <footer className="reviews-footer">
                <span className="material-icons reviews-footer-icon" aria-hidden="true">
                  {review.traits.icon}
                </span>
                <span className="reviews-footer-text">{review.traits.label}</span>
              </footer>
            </article>
          ))}
        </div>
      </div>

      <style jsx>{`
        .reviews-section {
          background: linear-gradient(
            135deg,
            rgba(32, 32, 32, 0.95),
            rgba(13, 13, 13, 0.9)
          );
          border-top: 1px solid var(--color-border);
          border-bottom: 1px solid var(--color-border);
          padding: ${SPACING_SECTION} ${SPACING_CARD};
          padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
          padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
          display: flex;
          justify-content: center;
        }

        .reviews-inner {
          width: 100%;
          max-width: ${MAX_CONTENT_WIDTH};
          display: flex;
          flex-direction: column;
          gap: ${SPACING_SECTION};
          color: var(--color-text-primary);
        }

        .reviews-header {
          display: flex;
          flex-direction: column;
          gap: ${SPACING_COMPONENT};
          max-width: 640px;
        }

        @media (min-width: 961px) {
          .reviews-header {
            align-self: flex-end;
            text-align: right;
            align-items: flex-end;
          }

          .reviews-button {
            align-self: flex-end;
          }
        }

        .reviews-eyebrow {
          margin: 0;
          display: inline-flex;
          align-items: center;
          gap: ${SPACING_ELEMENT};
          font-size: ${FONT_CAPTION};
          text-transform: uppercase;
          color: var(--color-text-muted);
        }

        .reviews-eyebrow-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: ${FONT_CAPTION};
          line-height: 1;
          letter-spacing: normal;
        }

        .reviews-eyebrow-text {
          letter-spacing: 0.2em;
        }

        .reviews-title {
          margin: 0;
          font-family: var(--font-family-base);
          font-size: ${FONT_TITLE};
          text-transform: uppercase;
        }

        .reviews-subtitle {
          margin: 0;
          color: var(--color-text-secondary);
          line-height: 1.6;
        }

        .reviews-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: ${SPACING_COMPONENT} ${SPACING_CARD_BODY};
          border-radius: 999px;
          background: var(--color-link);
          color: var(--color-accent-text);
          font-weight: 600;
          text-decoration: none;
          width: fit-content;
          transition: background 0.2s ease, transform 0.2s ease;
        }

        .reviews-button:hover,
        .reviews-button:focus {
          background: var(--color-link-hover);
          transform: translateY(-1px);
        }

        .reviews-button:focus {
          outline: 2px solid var(--color-link-hover);
          outline-offset: 2px;
        }

        .reviews-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: ${SPACING_GROUP};
        }

        .reviews-card {
          background: var(--color-card-background);
          border: 1px solid var(--color-border);
          border-radius: 16px;
          padding: ${SPACING_GROUP};
          display: flex;
          flex-direction: column;
          gap: ${SPACING_CARD_BODY};
          box-shadow: none;
        }

        .reviews-card-header {
          display: flex;
          justify-content: space-between;
          gap: ${SPACING_BUTTON};
          align-items: flex-start;
        }

        .reviews-name {
          margin: 0;
          font-size: ${FONT_TITLE3};
          font-weight: 600;
        }

        .reviews-location {
          margin: 4px 0 0;
          color: var(--color-text-secondary);
        }

        .reviews-meta {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: ${SPACING_ELEMENT};
        }

        .reviews-rating {
          display: flex;
          gap: 4px;
          color: #facc15;
          font-size: ${FONT_BODY};
          line-height: 1;
        }

        .reviews-star {
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .reviews-date {
          color: var(--color-text-muted);
          font-size: ${FONT_CALLOUT};
        }

        .reviews-body {
          display: flex;
          flex-direction: column;
          gap: ${SPACING_BUTTON};
          color: var(--color-text-secondary);
          line-height: 1.7;
        }

        .reviews-paragraph {
          margin: 0;
        }

        .reviews-highlight {
          background-color: #fef08a;
          padding: 0 2px;
          line-height: inherit;
          display: inline;
          box-decoration-break: clone;
          -webkit-box-decoration-break: clone;
          color: #854d0e;
        }

        :root[data-theme='dark'] .reviews-highlight {
          background-color: rgba(250, 204, 21, 0.25);
          color: #facc15;
        }

        .reviews-footer {
          display: flex;
          align-items: center;
          gap: ${SPACING_COMPONENT};
          padding-top: ${SPACING_CARD};
          border-top: 1px solid var(--color-border);
          color: var(--color-text-secondary);
        }

        .reviews-footer-icon {
          font-size: 1.25rem;
          color: var(--color-accent);
        }

        .reviews-footer-text {
          font-size: ${FONT_SUBHEADLINE};
        }

        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }

        @media (max-width: 960px) {
          .reviews-grid {
            grid-template-columns: 1fr;
          }

          .reviews-card {
            padding: ${SPACING_GROUP};
          }
        }

        @media (max-width: 600px) {
          .reviews-section {
            padding: ${SPACING_SECTION_LARGE} ${SPACING_CARD};
            padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
            padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
          }

          .reviews-title {
            font-size: ${FONT_LARGE_TITLE_MOBILE};
          }

          .reviews-card {
            padding: ${SPACING_CARD_BODY};
          }

          .reviews-card-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .reviews-meta {
            align-items: flex-start;
          }
        }
      `}</style>
    </section>
  );
}

// Grid item component similar to Browse page

function lightenColor(hex, mix = 0.12) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgb(${Math.round(r * mix + 255 * (1 - mix))}, ${Math.round(g * mix + 255 * (1 - mix))}, ${Math.round(b * mix + 255 * (1 - mix))})`;
}

function darkenColor(hex, mix = 0.45) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgb(${Math.round(r * mix)}, ${Math.round(g * mix)}, ${Math.round(b * mix)})`;
}

function BannersSection() {
  const { banners } = useContext(BannersContext);

  if (!banners || banners.length === 0) return null;

  return (
    <div className="banners-section">
      {banners.map((banner) => {
        const hasImage = !!banner.imageURL;
        const hasBg = !hasImage && !!banner.backgroundColor;
        const bgColor = hasBg ? lightenColor(banner.backgroundColor) : undefined;
        const titleColor = hasBg ? darkenColor(banner.backgroundColor, 0.45) : undefined;
        const subtitleColor = hasBg ? darkenColor(banner.backgroundColor, 0.6) : undefined;

        return (
          <div
            key={banner.id}
            className={`banner-item${hasImage ? ' banner-item--image' : ''}`}
            style={hasImage
              ? { backgroundImage: `url(${banner.imageURL})` }
              : { backgroundColor: bgColor }
            }
          >
            {hasImage && <div className="banner-overlay" />}
            <div className="banner-content">
              {banner.title && (
                <h2 className="banner-title" style={!hasImage && titleColor ? { color: titleColor } : undefined}>
                  {banner.title}
                </h2>
              )}
              {banner.subtitle && (
                <p className="banner-subtitle" style={!hasImage && subtitleColor ? { color: subtitleColor } : undefined}>
                  {banner.subtitle}
                </p>
              )}
            </div>
          </div>
        );
      })}
      <style jsx>{`
        .banners-section {
          width: 100vw;
          margin-left: calc(-50vw + 50%);
          display: flex;
          flex-direction: column;
          gap: 0;
        }
        .banner-item {
          position: relative;
          text-align: center;
          border-bottom: 1px solid var(--color-border);
        }
        .banner-item--image {
          background-size: cover;
          background-position: center;
          min-height: 320px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-bottom: none;
        }
        .banner-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.38);
        }
        .banner-content {
          position: relative;
          padding: ${SPACING_SECTION} ${SPACING_CARD};
          width: 100%;
        }
        .banner-item:not(.banner-item--image) .banner-content {
          padding: ${SPACING_SECTION} ${SPACING_CARD};
        }
        .banner-title {
          margin: 0 0 ${SPACING_ELEMENT} 0;
          font-size: ${FONT_TITLE2};
          font-weight: 600;
          font-family: var(--font-family-base);
          color: var(--color-text-primary);
        }
        .banner-item--image .banner-title {
          color: #fff;
        }
        .banner-subtitle {
          margin: 0 auto;
          font-size: ${FONT_HEADLINE};
          font-weight: 300;
          color: var(--color-text-secondary);
          max-width: ${MAX_CONTENT_WIDTH};
          line-height: 1.5;
        }
        .banner-item--image .banner-subtitle {
          color: rgba(255, 255, 255, 0.88);
        }
        @media (max-width: 768px) {
          .banner-item--image {
            min-height: 240px;
          }
          .banner-title {
            font-size: ${FONT_TITLE2_MOBILE};
          }
          .banner-subtitle {
            font-size: ${FONT_HEADLINE_MOBILE};
          }
        }
      `}</style>
    </div>
  );
}

export default function CatalogView() {
  usePageView('home');
  const navigate = useNavigate();
  const { homePreferences } = useContext(HomePreferencesContext);
  const { showUncategorized } = useDisplayPreferences();
  const { products, imageItems, getCoverImage } = useContext(ProductsContext);
  const [categories, setCategories] = useState([]);
  const [searchTerm] = useState('');
  const [selectedCategory] = useState('');
  const [loadedThumbnails, setLoadedThumbnails] = useState(new Set());
  const [popularProductIds, setPopularProductIds] = useState([]);

  // Get display style from home preferences
  const displayStyle = homePreferences?.displayStyle || DISPLAY_STYLE.GRID;

  // 1) Set document title on mount
  useEffect(() => {
    document.title = 'Home';
  }, []);

  // 2) Fetch all categories once
  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, 'categories'));
      const fetched = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(cat => !!(cat?.title || cat?.name))
        .sort((a, b) => {
          const titleA = (a.title || a.name || '').toLowerCase();
          const titleB = (b.title || b.name || '').toLowerCase();
          return titleA.localeCompare(titleB);
        });

      // Apply the same sorting logic as NavigationBar based on groupCategories setting
      const sortedCategories = groupCategories
        ? fetched // If grouping is on, use original alphabetical order
        : [...fetched].sort((a, b) => {
            // If grouping is off, sort with Product and Clearance at the bottom
            const titleA = (a.title || a.name || '').trim();
            const titleB = (b.title || b.name || '').trim();

            // Clearance always goes to the very bottom
            if (titleA === 'Clearance') return 1;
            if (titleB === 'Clearance') return -1;

            // Product goes above Clearance but below everything else
            if (titleA === 'Product') return 1;
            if (titleB === 'Product') return -1;

            // Everything else is sorted alphabetically
            return titleA.toLowerCase().localeCompare(titleB.toLowerCase());
          });

      setCategories(sortedCategories);
    })();
  }, []);

  // Fetch most popular product IDs
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'productStats'), orderBy('unitsSold', 'desc'), limit(8))
        );
        setPopularProductIds(snap.docs.map(d => d.id));
      } catch (err) {
        console.error('Error fetching popular products:', err);
      }
    })();
  }, []);

  // 3) When a thumbnail finishes loading, mark it as loaded
  const handleThumbnailLoad = productId => {
    setLoadedThumbnails(prev => {
      const next = new Set(prev);
      next.add(productId);
      return next;
    });
  };

  const search = searchTerm.trim().toLowerCase();

  const isProductActive = product => {
    const deactivatedDate = parseDate(product.deactivatedDate);
    if (!deactivatedDate) return true;
    const today = new Date();
    return today <= deactivatedDate;
  };

  const activeProducts = products.filter(isProductActive);

  // Popular products: preserve popularity order, only show active products
  const popularProducts = popularProductIds
    .map(id => activeProducts.find(p => p.id === id))
    .filter(Boolean);

  // 7) Filter + sort logic
  let filteredProducts = [];
  if (selectedCategory) {
    const cat = categories.find(c => c.id === selectedCategory);
    if (cat) {
      filteredProducts = activeProducts
        .filter(
          prod =>
            prod.productName.toLowerCase().includes(search) &&
            Array.isArray(cat.itemIDs) &&
            cat.itemIDs.includes(prod.id)
        )
        .sort((a, b) => a.productName.localeCompare(b.productName));
    }
  } else {
    filteredProducts = activeProducts
      .filter(prod => prod.productName.toLowerCase().includes(search))
      .sort((a, b) => a.productName.localeCompare(b.productName));
  }

  // Check if hero section should be shown
  const shouldShowHero = homePreferences?.heroTitle ||
                         homePreferences?.heroSubtitle ||
                         homePreferences?.showCarousel ||
                         homePreferences?.heroBackgroundImageURL;

  return (
    <div>
      {shouldShowHero && (
        <HeroSection
          products={activeProducts}
          imageItems={imageItems}
          getCoverImage={getCoverImage}
          categories={categories}
          homePreferences={homePreferences}
        />
      )}

      <BannersSection />

      {/* <InstagramSection /> */}

      {/* <ReviewsSection /> */}

      {/* Most Popular section — uncomment when ready
      {popularProducts.length > 0 && (
        <div style={{ marginBottom: '24px' }} className="product-list-container">
          <h2
            style={{
              margin: 0,
              padding: 0,
              marginBottom: displayStyle === DISPLAY_STYLE.GRID ? '12px' : '0px',
              fontFamily: 'var(--font-family-base)',
              fontWeight: 600,
              fontSize: FONT_TITLE3,
              textTransform: 'none',
              letterSpacing: 'normal',
            }}
          >
            Most Popular
          </h2>
          {displayStyle === DISPLAY_STYLE.GRID ? (
            <div className="product-grid">
              {popularProducts.map(product => (
                <ProductGridItem
                  key={product.id}
                  product={product}
                  coverImage={getCoverImage(product)}
                  onClick={() => navigate(buildProductPath(product))}
                  isNew={isProductNew(product)}
                />
              ))}
            </div>
          ) : (
            popularProducts.map(product => (
              <ProductRow
                key={product.id}
                product={product}
                coverImage={getCoverImage(product)}
                isLoaded={loadedThumbnails.has(product.id)}
                onImageLoad={handleThumbnailLoad}
                isNew={isProductNew(product)}
              />
            ))
          )}
        </div>
      )}
      */}

      <div className="product-list-container">
        {selectedCategory === '' ? (
          <>
            {categories.map(cat => {
              const productsInThisCategory = filteredProducts.filter(prod =>
                Array.isArray(cat.itemIDs) && cat.itemIDs.includes(prod.id)
              );
              if (productsInThisCategory.length === 0) {
                return null;
              }
              return (
                <div key={cat.id} style={{ marginBottom: '24px' }}>
                  <h2
                    style={{
                      margin: 0,
                      padding: 0,
                      marginBottom: displayStyle === DISPLAY_STYLE.GRID ? '12px' : '0px',
                      fontFamily: 'var(--font-family-base)',
                      fontWeight: 600,
                      fontSize: FONT_TITLE3,
                      textTransform: 'none',
                      letterSpacing: 'normal',
                    }}
                  >
                    <Link to={`/category/${cat.id}`} className="category-link">
                      {cat.title}
                    </Link>
                  </h2>
                  {typeof cat.subtitle === 'string' && cat.subtitle.trim() ? (
                    <p className="category-subtitle">{cat.subtitle}</p>
                  ) : null}
                  {displayStyle === DISPLAY_STYLE.GRID ? (
                    <div className="product-grid">
                      {productsInThisCategory.map(product => (
                        <ProductGridItem
                          key={product.id}
                          product={product}
                          coverImage={getCoverImage(product)}
                          onClick={() => navigate(buildProductPath(product))}
                          isNew={isProductNew(product)}
                        />
                      ))}
                    </div>
                  ) : (
                    productsInThisCategory.map(product => (
                      <ProductRow
                        key={product.id}
                        product={product}
                        coverImage={getCoverImage(product)}
                        isLoaded={loadedThumbnails.has(product.id)}
                        onImageLoad={handleThumbnailLoad}
                        isNew={isProductNew(product)}
                      />
                    ))
                  )}
                </div>
              );
            })}
            {(() => {
              if (!showUncategorized) return null;
              const allCategorizedIDs = new Set(categories.flatMap(c => c.itemIDs || []));
              const uncategorized = filteredProducts.filter(p => !allCategorizedIDs.has(p.id));
              if (uncategorized.length === 0) return null;
              return (
                <div style={{ marginBottom: '24px' }}>
                  <h2
                    style={{
                      margin: 0,
                      padding: 0,
                      marginBottom: displayStyle === DISPLAY_STYLE.GRID ? '12px' : '0px',
                      fontFamily: 'var(--font-family-base)',
                      fontWeight: 600,
                      fontSize: FONT_TITLE3,
                      textTransform: 'none',
                      letterSpacing: 'normal',
                    }}
                  >
                    More
                  </h2>
                  {displayStyle === DISPLAY_STYLE.GRID ? (
                    <div className="product-grid">
                      {uncategorized.map(product => (
                        <ProductGridItem
                          key={product.id}
                          product={product}
                          coverImage={getCoverImage(product)}
                          onClick={() => navigate(buildProductPath(product))}
                          isNew={isProductNew(product)}
                        />
                      ))}
                    </div>
                  ) : (
                    uncategorized.map(product => (
                      <ProductRow
                        key={product.id}
                        product={product}
                        coverImage={getCoverImage(product)}
                        isLoaded={loadedThumbnails.has(product.id)}
                        onImageLoad={handleThumbnailLoad}
                        isNew={isProductNew(product)}
                      />
                    ))
                  )}
                </div>
              );
            })()}
          </>
        ) : (
          displayStyle === DISPLAY_STYLE.GRID ? (
            <div className="product-grid">
              {filteredProducts.map(product => (
                <ProductGridItem
                  key={product.id}
                  product={product}
                  coverImage={getCoverImage(product)}
                  onClick={() => navigate(buildProductPath(product))}
                  isNew={isProductNew(product)}
                />
              ))}
            </div>
          ) : (
            filteredProducts.map(product => (
              <ProductRow
                key={product.id}
                product={product}
                coverImage={getCoverImage(product)}
                isLoaded={loadedThumbnails.has(product.id)}
                onImageLoad={handleThumbnailLoad}
                isNew={isProductNew(product)}
              />
            ))
          )
        )}

        <style jsx>{`
          .product-list-container {
            max-width: ${MAX_CONTENT_WIDTH};
            margin: 0 auto;
            padding: ${SPACING_CARD_BODY} ${SPACING_CARD} ${SPACING_SECTION};
            padding-left: max(16px, env(safe-area-inset-left));
            padding-right: max(16px, env(safe-area-inset-right));
          }

          .category-link {
            color: inherit;
            text-decoration: none;
          }

          .category-link:hover,
          .category-link:focus {
            text-decoration: underline;
          }

          .category-subtitle {
            margin: 0 0 ${SPACING_COMPONENT} 0;
            color: var(--color-text-secondary);
          }

          /* Grid layout styles */
          .product-grid {
            display: grid;
            width: 100%;
            row-gap: ${SPACING_BUTTON};
            column-gap: ${SPACING_BUTTON};
            grid-template-columns: repeat(4, 1fr);
          }

          @media (min-width: 1400px) {
            .product-grid {
              grid-template-columns: repeat(5, 1fr);
              column-gap: ${SPACING_BUTTON};
              row-gap: ${SPACING_BUTTON};
            }
          }

          @media (min-width: 1000px) and (max-width: 1399px) {
            .product-grid {
              grid-template-columns: repeat(4, 1fr);
              column-gap: ${SPACING_BUTTON};
              row-gap: ${SPACING_BUTTON};
            }
          }

          @media (max-width: 999px) {
            .product-grid {
              grid-template-columns: repeat(3, 1fr);
              column-gap: ${SPACING_BUTTON};
              row-gap: ${SPACING_BUTTON};
            }
          }

          @media (max-width: 600px) {
            .product-grid {
              grid-template-columns: repeat(2, 1fr);
              column-gap: ${SPACING_BUTTON};
              row-gap: ${SPACING_BUTTON};
            }
          }

        `}</style>
      </div>
    </div>
  );
}
